// DES: security description.

import { quotes, summary, type Quote, type Summary } from "../api";
import { clear, h, kv, link, loading, rangeBar, type Child } from "../dom";
import * as f from "../fmt";
import { register, type Ctx } from "../registry";
import { DataTable } from "../table";

function priceBlock(q: Quote): HTMLElement {
  const ch = q.regularMarketChange;
  const ext =
    q.marketState?.startsWith("PRE") && q.preMarketPrice
      ? ["Pre", q.preMarketPrice, q.preMarketChangePercent]
      : q.marketState?.startsWith("POST") && q.postMarketPrice
        ? ["After hours", q.postMarketPrice, q.postMarketChangePercent]
        : null;
  return h(
    "div",
    { class: "bigq" },
    h("span", { class: "px" }, f.price(q.regularMarketPrice, q.priceHint)),
    h("span", { class: `ch ${f.cls(ch)}` }, f.chg(ch, q.priceHint), " ", f.pct(q.regularMarketChangePercent)),
    h("span", { class: "dim" }, q.currency ?? "", " · ", q.fullExchangeName ?? q.exchange ?? "", " · ", q.marketState ?? ""),
    h("span", { class: "dim" }, "at ", f.stamp(q.regularMarketTime)),
    ext
      ? h("span", null, h("span", { class: "amber" }, `${ext[0]} `), f.price(ext[1]), " ", h("span", { class: f.cls(ext[2]) }, f.pct(ext[2])))
      : null,
  );
}

function rangeRow(lo?: number, hi?: number, v?: number, dp?: number): Child {
  if (lo === undefined || hi === undefined) return null;
  return h("span", { style: { display: "inline-flex", gap: "6px", alignItems: "center" } }, f.price(lo, dp), rangeBar(lo, hi, v, 70), f.price(hi, dp));
}

const upside = (target?: number, px?: number) => (target && px ? f.pct((target / px - 1) * 100, 1) : null);

function sections(q: Quote, s: Summary | null): HTMLElement {
  const sd = s?.summaryDetail ?? {};
  const ks = s?.defaultKeyStatistics ?? {};
  const fd = s?.financialData ?? {};
  const ce = s?.calendarEvents ?? {};
  const px = q.regularMarketPrice;
  const dp = q.priceHint;
  const p = (v: unknown) => f.price(v, dp);
  const isEquity = q.quoteType === "EQUITY";
  const isFund = q.quoteType === "ETF" || q.quoteType === "MUTUALFUND";

  const blocks: HTMLElement[] = [
    h(
      "div",
      null,
      h("h3", null, "Price"),
      kv([
        ["Open", p(q.regularMarketOpen)],
        ["Prev close", p(q.regularMarketPreviousClose)],
        ["Day range", rangeRow(q.regularMarketDayLow, q.regularMarketDayHigh, px, dp)],
        ["52w range", rangeRow(q.fiftyTwoWeekLow, q.fiftyTwoWeekHigh, px, dp)],
        ["52w change", q.fiftyTwoWeekChangePercent !== undefined ? h("span", { class: f.cls(q.fiftyTwoWeekChangePercent) }, f.pct(q.fiftyTwoWeekChangePercent)) : null],
        ["Bid × size", q.bid ? (q.bidSize ? `${p(q.bid)} × ${f.int(q.bidSize)}` : p(q.bid)) : null],
        ["Ask × size", q.ask ? (q.askSize ? `${p(q.ask)} × ${f.int(q.askSize)}` : p(q.ask)) : null],
        ["Volume", q.regularMarketVolume ? f.int(q.regularMarketVolume) : null],
        ["Avg vol 3M", q.averageDailyVolume3Month ? f.int(q.averageDailyVolume3Month) : null],
        ["50d / 200d avg", q.fiftyDayAverage ? `${p(q.fiftyDayAverage)} / ${p(q.twoHundredDayAverage)}` : null],
        ["Open interest", q.openInterest ? f.int(q.openInterest) : null],
        ["Expires", q.expireDate ? f.utcDate(q.expireDate) : null],
      ]),
    ),
  ];

  if (isEquity) {
    blocks.push(
      h(
        "div",
        null,
        h("h3", null, "Valuation"),
        kv([
          ["Market cap", f.big(q.marketCap)],
          ["Enterprise value", f.big(ks.enterpriseValue)],
          ["P/E (ttm)", f.fixedN(q.trailingPE)],
          ["P/E (fwd)", f.fixedN(q.forwardPE)],
          ["PEG", f.fixedN(ks.pegRatio)],
          ["P/B", f.fixedN(q.priceToBook)],
          ["P/S (ttm)", f.fixedN(sd.priceToSalesTrailing12Months)],
          ["EV/EBITDA", f.fixedN(ks.enterpriseToEbitda)],
          ["EV/Revenue", f.fixedN(ks.enterpriseToRevenue)],
          ["EPS (ttm)", f.price(q.epsTrailingTwelveMonths)],
          ["EPS (fwd)", f.price(q.epsForward)],
        ]),
      ),
      h(
        "div",
        null,
        h("h3", null, "Fundamentals"),
        kv([
          ["Revenue (ttm)", f.big(fd.totalRevenue)],
          ["Revenue growth", f.ratio(fd.revenueGrowth, 1)],
          ["Gross margin", f.ratio(fd.grossMargins, 1)],
          ["Operating margin", f.ratio(fd.operatingMargins, 1)],
          ["Profit margin", f.ratio(fd.profitMargins, 1)],
          ["ROE", f.ratio(fd.returnOnEquity, 1)],
          ["ROA", f.ratio(fd.returnOnAssets, 1)],
          ["Free cash flow", f.big(fd.freeCashflow)],
          ["Cash", f.big(fd.totalCash)],
          ["Debt", f.big(fd.totalDebt)],
          ["Debt/Equity", f.fixedN(fd.debtToEquity)],
          ["Current ratio", f.fixedN(fd.currentRatio)],
        ]),
      ),
      h(
        "div",
        null,
        h("h3", null, "Dividends & risk"),
        kv([
          ["Dividend yield", sd.dividendYield ? f.ratio(sd.dividendYield) : null],
          ["Dividend rate", sd.dividendRate ? f.price(sd.dividendRate) : null],
          ["Ex-dividend", sd.exDividendDate ? f.utcDate(sd.exDividendDate) : null],
          ["Payout ratio", sd.payoutRatio ? f.ratio(sd.payoutRatio, 1) : null],
          ["Beta", f.fixedN(sd.beta ?? ks.beta)],
          ["Shares out", f.big(q.sharesOutstanding ?? ks.sharesOutstanding)],
          ["Float", f.big(ks.floatShares)],
          ["Short % float", ks.shortPercentOfFloat ? f.ratio(ks.shortPercentOfFloat) : null],
          ["Short ratio", f.fixedN(ks.shortRatio)],
          ["Insiders", ks.heldPercentInsiders ? f.ratio(ks.heldPercentInsiders) : null],
          ["Institutions", ks.heldPercentInstitutions ? f.ratio(ks.heldPercentInstitutions) : null],
        ]),
      ),
      h(
        "div",
        null,
        h("h3", null, "Analysts & events"),
        kv([
          ["Consensus", fd.recommendationKey && fd.recommendationKey !== "none" ? `${String(fd.recommendationKey).toUpperCase().replace("_", " ")} (${f.fixedN(fd.recommendationMean)})` : q.averageAnalystRating],
          ["Analysts", f.int(fd.numberOfAnalystOpinions)],
          ["Target mean", fd.targetMeanPrice ? h("span", null, f.price(fd.targetMeanPrice), " ", h("span", { class: f.cls((fd.targetMeanPrice ?? 0) - (px ?? 0)) }, upside(fd.targetMeanPrice, px))) : null],
          ["Target range", fd.targetLowPrice ? `${f.price(fd.targetLowPrice)} – ${f.price(fd.targetHighPrice)}` : null],
          ["Next earnings", ce.earnings?.earningsDate?.[0] ? f.date(ce.earnings.earningsDate[0]) : q.earningsTimestamp ? f.date(q.earningsTimestamp) : null],
          ["EPS estimate", ce.earnings?.earningsAverage !== undefined ? f.price(ce.earnings.earningsAverage) : null],
          ["Revenue estimate", ce.earnings?.revenueAverage ? f.big(ce.earnings.revenueAverage) : null],
        ]),
      ),
    );
  } else if (isFund) {
    const fp = s?.fundProfile ?? {};
    blocks.push(
      h(
        "div",
        null,
        h("h3", null, "Fund"),
        kv([
          ["Family", fp.family],
          ["Category", fp.categoryName ?? ks.category],
          ["Net assets", f.big(q.netAssets ?? sd.totalAssets)],
          ["Expense ratio", q.netExpenseRatio !== undefined ? `${f.fixedN(q.netExpenseRatio)}%` : fp.feesExpensesInvestment?.annualReportExpenseRatio !== undefined ? f.ratio(fp.feesExpensesInvestment.annualReportExpenseRatio) : null],
          ["Yield", sd.yield ? f.ratio(sd.yield) : null],
          ["YTD return", q.ytdReturn !== undefined ? f.pct(q.ytdReturn) : null],
          ["Beta (3y)", f.fixedN(ks.beta3Year)],
          ["Inception", ks.fundInceptionDate ? f.utcDate(ks.fundInceptionDate) : null],
        ]),
      ),
    );
  } else {
    blocks.push(
      h(
        "div",
        null,
        h("h3", null, "Instrument"),
        kv([
          ["Type", q.typeDisp ?? q.quoteType],
          ["Exchange", q.fullExchangeName],
          ["Currency", q.currency],
          ["Market cap", q.marketCap ? f.big(q.marketCap) : null],
          ["Circulating", q.circulatingSupply ? f.big(q.circulatingSupply) : null],
          ["Volume 24h", q.volume24Hr ? f.big(q.volume24Hr) : null],
          ["Data", q.quoteSourceName],
          ["Delay", q.exchangeDataDelayedBy ? `${q.exchangeDataDelayedBy} min` : null],
        ]),
      ),
    );
  }
  return h("div", { class: "cols" }, ...blocks);
}

function profile(s: Summary): HTMLElement | null {
  const ap = s.assetProfile ?? s.summaryProfile;
  if (!ap) return null;
  const hq = [ap.city, ap.state, ap.country].filter(Boolean).join(", ");
  return h(
    "div",
    null,
    h("h3", null, "Profile"),
    h(
      "div",
      { class: "cols" },
      kv([
        ["Sector", ap.sectorDisp ?? ap.sector],
        ["Industry", ap.industryDisp ?? ap.industry],
        ["Employees", ap.fullTimeEmployees ? f.int(ap.fullTimeEmployees) : null],
      ]),
      kv([
        ["Headquarters", hq],
        ["Website", ap.website ? link(ap.website, String(ap.website).replace(/^https?:\/\/(www\.)?/, "")) : null],
        ["Phone", ap.phone],
      ]),
    ),
    ap.longBusinessSummary ? h("p", { class: "prose" }, ap.longBusinessSummary) : null,
  );
}

interface Officer {
  name?: string;
  title?: string;
  age?: number;
  totalPay?: number;
}

function officers(s: Summary): HTMLElement | null {
  const list: Officer[] = s.assetProfile?.companyOfficers ?? [];
  if (!list.length) return null;
  const t = new DataTable<Officer>(
    [
      { label: "Name", get: (o) => o.name },
      { label: "Title", get: (o) => o.title },
      { label: "Age", get: (o) => o.age, num: true },
      { label: "Pay", get: (o) => o.totalPay, fmt: (v: number) => f.big(v), num: true },
    ],
    { sortable: false },
  );
  t.set(list.slice(0, 8));
  return h("div", null, h("h3", null, "Officers"), t.el);
}

interface Holding {
  symbol?: string;
  holdingName?: string;
  holdingPercent?: number;
}

function holdings(ctx: Ctx, s: Summary): HTMLElement | null {
  const list: Holding[] = s.topHoldings?.holdings ?? [];
  if (!list.length) return null;
  const t = new DataTable<Holding>(
    [
      { label: "Symbol", get: (x) => x.symbol },
      { label: "Holding", get: (x) => x.holdingName },
      { label: "Weight", get: (x) => x.holdingPercent, fmt: (v: number) => f.ratio(v), num: true },
    ],
    { sortable: false, panel: ctx.panel, action: (x) => (x.symbol ? `${x.symbol} DES` : null) },
  );
  t.set(list);
  return h("div", null, h("h3", null, "Top holdings"), t.el);
}

function related(ctx: Ctx, q: Quote): HTMLElement {
  const equity = q.quoteType === "EQUITY";
  const fns: [string, string][] = equity
    ? [
        ["GP", "Price graph"],
        ["FA", "Financials"],
        ["CN", "News"],
        ["ANR", "Analysts"],
        ["HDS", "Holders"],
        ["RV", "Peers"],
        ["OMON", "Options"],
        ["CF", "SEC filings"],
        ["HP", "Price history"],
        ["COMP", "Compare"],
      ]
    : [
        ["GP", "Price graph"],
        ["GIP", "Intraday"],
        ["HP", "Price history"],
        ["COMP", "Compare"],
        ["CN", "News"],
      ];
  return h(
    "div",
    { class: "toolbar", style: { borderBottom: "none", padding: "6px 0" } },
    ...fns.map(([code, label]) => {
      const n = ctx.panel.action(`${ctx.sec} ${code}`);
      return h("button", { onclick: () => ctx.panel.go(`${ctx.sec} ${code}`), title: label }, h("span", { class: "yellow" }, `${n}) `), code, h("span", { class: "dim" }, ` ${label}`));
    }),
  );
}

async function run(ctx: Ctx): Promise<void> {
  const sym = ctx.sec!;
  clear(ctx.body, loading());
  const [qr, sr] = await Promise.all([quotes([sym], ctx.signal), summary(sym, ctx.signal).catch(() => null)]);
  if (ctx.signal.aborted) return;
  const q = qr.data[0];
  if (!q) throw new Error(`Unknown security: ${sym}`);
  const s = sr?.data ?? null;
  const priceEl = h("div");
  const paint = (q: Quote) => clear(priceEl, priceBlock(q));
  paint(q);
  const name = q.longName ?? q.shortName ?? sym;
  clear(
    ctx.body,
    h(
      "div",
      { class: "pad" },
      h("div", { style: { fontSize: "16px", marginBottom: "4px" } }, h("span", { class: "amber" }, name), " ", h("span", { class: "dim" }, q.typeDisp ?? "")),
      priceEl,
      related(ctx, q),
      sections(q, s),
      s ? profile(s) : null,
      s && q.quoteType === "EQUITY" ? officers(s) : null,
      s ? holdings(ctx, s) : null,
    ),
  );
  ctx.panel.status(`Yahoo · ${q.quoteSourceName ?? ""}${q.exchangeDataDelayedBy ? ` · delayed ${q.exchangeDataDelayedBy}m` : ""}`, qr.meta);
  ctx.panel.every(15_000, async () => {
    const r = await quotes([sym], ctx.signal);
    if (r.data[0]) paint(r.data[0]);
  });
}

register({
  code: "DES",
  name: "Description",
  group: "Equity",
  needsSec: true,
  help: "Company or instrument overview: price, valuation, fundamentals, profile.",
  run,
});
